import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useCaptions } from '../context/CaptionContext';
import ArabicKeyboard from './ArabicKeyboard';
import { useI18n, rich } from '../../i18n';
import './WordPopup.css';

// Helper to detect if text is primarily Latin
const isLatinText = (text) => {
  if (!text) return false;
  // Match Latin characters (including accented), numbers
  const latinRegex = /^[a-zA-ZÀ-ÿĀ-žḀ-ỿ0-9\s.,!?;:'"()\-–—«»„""''‹›]+$/;
  return latinRegex.test(text.trim());
};

// Punctuation characters for each script
const ARABIC_PUNCTUATION = ['،', '؛', ':', '؟', '!', '.', '«', '»', '-', '–', '"', "'", '(', ')', '[', ']'];
const LATIN_PUNCTUATION = [',', ';', ':', '?', '!', '.', '"', "'", '-', '–', '(', ')', '[', ']', '/', '...'];

const WordPopup = ({ wordId, skeletonIndex, anchorRect, isNonArabic = false, onClose }) => {
  const { t } = useI18n();
  const {
    captionData,
    getWordById,
    updateWord,
    updateSkeleton,
    updateCaptionData,
    splitSkeleton,
    mergeWithPrevious,
    addWord,
    splitWord,
    removeWord,
    mergeWords,
    getPhraseForWord,
    selectPhraseAlternative,
    mergeSkeletons,
    mergeSkeletonsAndApplyAlternative,
    getDisplayText,
    displayMode,
    videoRef,
    seekToTime,
    themeSettings,
    markWordsAsChanged
  } = useCaptions();

  const isArabizi = displayMode === 'arabizi';

  const word = getWordById(wordId);
  const skeleton = captionData.skeleton[skeletonIndex];
  const wordIndexInSkeleton = skeleton?.words.indexOf(wordId) ?? -1;

  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [editText, setEditText] = useState('');
  const [overlapWarning, setOverlapWarning] = useState(null);
  const [pendingChange, setPendingChange] = useState(null);
  const [boundaryConfirm, setBoundaryConfirm] = useState(null);
  const [hasUnsavedTimeChanges, setHasUnsavedTimeChanges] = useState(false);
  const [showPhraseAlternatives, setShowPhraseAlternatives] = useState(false);
  const [phraseMergeConfirm, setPhraseMergeConfirm] = useState(null);
  const [pendingPhraseAlternative, setPendingPhraseAlternative] = useState(null);
  const [editingPunctuation, setEditingPunctuation] = useState(false);
  const [punctuationText, setPunctuationText] = useState('');
  const punctuationInputRef = useRef(null);
  
  // Replace all occurrences feature
  const [originalTextBeforeEdit, setOriginalTextBeforeEdit] = useState('');
  const [showReplaceAllModal, setShowReplaceAllModal] = useState(false);
  const [replaceAllInfo, setReplaceAllInfo] = useState({ count: 0, newText: '', matchingWordIds: [] });
  
  const popupRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [maxHeight, setMaxHeight] = useState(null);

  // Determine if this word is at skeleton boundaries
  const isFirstWordInSkeleton = wordIndexInSkeleton === 0;
  const isLastWordInSkeleton = wordIndexInSkeleton === skeleton?.words.length - 1;

  // Check if this is a punctuation word
  const isPunctuation = word?.type === 'punctuation';

  // Determine if word can be merged with adjacent words
  const canMergeWithPrevious = wordIndexInSkeleton > 0 && !isPunctuation;
  const canMergeWithNext = skeleton && wordIndexInSkeleton < skeleton.words.length - 1 && !isPunctuation;

  // Check if word can be split (contains internal spaces in current display mode)
  const canSplitWord = useMemo(() => {
    if (!word || isPunctuation) return false;
    // Check the text for the current display mode
    const textToCheck = isArabizi ? (word.arabizi || word.text || '') : (word.text || '');
    const trimmed = textToCheck.trim();
    // Check if there are spaces inside the trimmed text
    return /\s/.test(trimmed);
  }, [word, isPunctuation, isArabizi]);

  // Check if this word is an auto speaker-change point (previous non-silence word has a different speaker)
  const isAutoSpeakerChange = useMemo(() => {
    if (!word || !word.speaker || !themeSettings.speakerDashEnabled) return false;
    const allSkeletons = captionData.skeleton;
    let prevSpeaker = null;
    for (const skel of allSkeletons) {
      for (const wid of skel.words) {
        const w = captionData.words.find(x => x.id === wid);
        if (!w || w.type === 'silence' || w.type === 'punctuation') continue;
        if (wid === wordId) {
          return prevSpeaker !== null && w.speaker !== prevSpeaker;
        }
        if (w.speaker) prevSpeaker = w.speaker;
      }
    }
    return false;
  }, [word, wordId, captionData.skeleton, captionData.words, themeSettings.speakerDashEnabled]);

  // Find attached punctuation (punctuation word that comes after this word)
  const attachedPunctuation = useMemo(() => {
    if (!skeleton || !word || isPunctuation) return null;
    
    const nextWordIndex = wordIndexInSkeleton + 1;
    if (nextWordIndex >= skeleton.words.length) return null;
    
    const nextWordId = skeleton.words[nextWordIndex];
    const nextWord = captionData.words.find(w => w.id === nextWordId);
    
    if (nextWord?.type === 'punctuation' && nextWord?.attaches_to === 'previous') {
      return nextWord;
    }
    return null;
  }, [skeleton, word, wordIndexInSkeleton, captionData.words, isPunctuation]);

  // Get adjacent words for overlap detection
  const adjacentWords = useMemo(() => {
    if (!skeleton) return { prev: null, next: null };
    
    const allWords = captionData.words;
    const currentIndex = allWords.findIndex(w => w.id === wordId);
    
    // Find previous word (by time)
    const wordsBefore = allWords
      .filter(w => w.id !== wordId && (w.end_time || 0) <= (word?.start_time || 0))
      .sort((a, b) => (b.end_time || 0) - (a.end_time || 0));
    
    // Find next word (by time)
    const wordsAfter = allWords
      .filter(w => w.id !== wordId && (w.start_time || 0) >= (word?.end_time || 0))
      .sort((a, b) => (a.start_time || 0) - (b.start_time || 0));
    
    return {
      prev: wordsBefore[0] || null,
      next: wordsAfter[0] || null
    };
  }, [captionData.words, skeleton, wordId, word]);

  // Get phrase for this word (if any)
  const phrase = useMemo(() => {
    return getPhraseForWord(wordId);
  }, [getPhraseForWord, wordId]);

  // Compute phrase display text
  const phraseDisplayInfo = useMemo(() => {
    if (!phrase) return null;
    
    const currentText = phrase.currentWordIds
      .map(id => {
        const w = captionData.words.find(w => w.id === id);
        return isArabizi ? (w?.arabizi || w?.text || '') : (w?.text || '');
      })
      .join(' ');
    
    const originalText = phrase.originalWords
      .map(w => isArabizi ? (w.arabizi || w.text) : w.text)
      .join(' ');
    
    return {
      currentText,
      originalText,
      hasAlternatives: phrase.alternatives && phrase.alternatives.length > 0,
      isOriginalActive: phrase.activeAlternativeId === null
    };
  }, [phrase, captionData.words, isArabizi]);

  // Determine if we should show Latin punctuation
  const useLatinPunctuation = useMemo(() => {
    // Check if word is part of a Latin phrase (code_switched active)
    if (phrase?.activeAlternativeId === 'alt_code_switched') {
      return true;
    }
    // Check if the word text itself is Latin
    if (word && isLatinText(word.text)) {
      return true;
    }
    return false;
  }, [phrase, word]);

  // Punctuation characters based on script
  const punctuationChars = useLatinPunctuation ? LATIN_PUNCTUATION : ARABIC_PUNCTUATION;

  // Position popup near the word
  useEffect(() => {
    if (anchorRect && popupRef.current) {
      const popup = popupRef.current;
      const popupRect = popup.getBoundingClientRect();
      
      const topBarOffset = 72; // keep below top bar
      let left = anchorRect.left + (anchorRect.width / 2) - (popupRect.width / 2);

      // Keep within viewport horizontally
      const minLeft = 8;
      const maxLeft = window.innerWidth - popupRect.width - 8;
      
      if (left < minLeft) {
        left = minLeft;
      } else if (left > maxLeft && maxLeft > minLeft) {
        left = maxLeft;
      }

      // Keep within viewport vertically (always below the word, but not above top bar)
      const spaceBelow = window.innerHeight - anchorRect.bottom - 16;
      let calculatedMaxHeight = null;
      
      // Place below the word, but ensure it stays below the top bar
      let top = anchorRect.bottom + 8;
      if (top < topBarOffset) {
        top = topBarOffset;
      }
      calculatedMaxHeight = Math.max(window.innerHeight - top - 16, 120);

      setPosition({ top, left });
      setMaxHeight(calculatedMaxHeight);
    }
  }, [anchorRect, showKeyboard, overlapWarning, boundaryConfirm, editingPunctuation, showPhraseAlternatives]);

  // Update local state when word changes
  useEffect(() => {
    if (word) {
      setStartTime(word.start_time?.toFixed(2) || '0.00');
      setEndTime(word.end_time?.toFixed(2) || '0.00');
      // Use arabizi text when in arabizi mode, otherwise use original text
      setEditText(isArabizi ? (word.arabizi || word.text || '') : (word.text || ''));
    }
  }, [word?.id, isArabizi]);

  // Close popup if word or skeleton no longer exists (e.g., after phrase replacement)
  useEffect(() => {
    if (!word || !skeleton) {
      onClose();
    }
  }, [word, skeleton, onClose]);

  if (!word || !skeleton) return null;

  // Check for overlaps and boundary changes when modifying time
  const checkOverlap = (field, newValue) => {
    const numValue = parseFloat(newValue);
    if (isNaN(numValue) || numValue < 0) return null;

    const newStart = field === 'start_time' ? numValue : word.start_time;
    const newEnd = field === 'end_time' ? numValue : word.end_time;

    // Check if end is before start
    if (newEnd < newStart) {
      return {
        type: 'invalid',
        message: t('End time cannot be before start time')
      };
    }

    // Check overlap with previous word (skip if it's a silence)
    if (adjacentWords.prev && field === 'start_time' && adjacentWords.prev.type !== 'silence') {
      if (numValue < adjacentWords.prev.end_time) {
        return {
          type: 'overlap_prev',
          message: t('Overlaps with previous word "{word}" (ends at {time}s)', { word: adjacentWords.prev.text, time: adjacentWords.prev.end_time.toFixed(2) }),
          suggestion: adjacentWords.prev.end_time,
          affectedWord: adjacentWords.prev
        };
      }
    }

    // Check overlap with next word (skip if it's a silence)
    if (adjacentWords.next && field === 'end_time' && adjacentWords.next.type !== 'silence') {
      if (numValue > adjacentWords.next.start_time) {
        return {
          type: 'overlap_next',
          message: t('Overlaps with next word "{word}" (starts at {time}s)', { word: adjacentWords.next.text, time: adjacentWords.next.start_time.toFixed(2) }),
          suggestion: adjacentWords.next.start_time,
          affectedWord: adjacentWords.next
        };
      }
    }

    return null;
  };

  // Check if boundary change is needed
  const checkBoundaryChange = (field, newValue) => {
    const numValue = parseFloat(newValue);
    if (isNaN(numValue) || numValue < 0) return null;

    // Changing start time of first word affects skeleton start
    if (field === 'start_time' && isFirstWordInSkeleton) {
      if (adjacentWords.prev?.type === 'silence') {
        return null;
      }
      const oldSkeletonStart = parseFloat(skeleton.start_time) || 0;
      const oldWordStart = parseFloat(word.start_time) || 0;
      // Check if the new value is different from current word start time
      if (Math.abs(numValue - oldWordStart) > 0.001) {
        return {
          type: 'skeleton_start',
          message: t('This will change the chunk start time from {from}s to {to}s', { from: oldSkeletonStart.toFixed(2), to: numValue.toFixed(2) }),
          oldValue: oldSkeletonStart,
          newValue: numValue
        };
      }
    }

    // Changing end time of last word affects skeleton end
    if (field === 'end_time' && isLastWordInSkeleton) {
      if (adjacentWords.next?.type === 'silence') {
        return null;
      }
      const oldSkeletonEnd = parseFloat(skeleton.end_time) || 0;
      const oldWordEnd = parseFloat(word.end_time) || 0;
      // Check if the new value is different from current word end time
      if (Math.abs(numValue - oldWordEnd) > 0.001) {
        return {
          type: 'skeleton_end',
          message: t('This will change the chunk end time from {from}s to {to}s', { from: oldSkeletonEnd.toFixed(2), to: numValue.toFixed(2) }),
          oldValue: oldSkeletonEnd,
          newValue: numValue
        };
      }
    }

    return null;
  };

  const handleTimeInputChange = (field, value) => {
    if (field === 'start_time') {
      setStartTime(value);
      const originalValue = word.start_time?.toFixed(2) || '0.00';
      setHasUnsavedTimeChanges(value !== originalValue || endTime !== (word.end_time?.toFixed(2) || '0.00'));
    } else {
      setEndTime(value);
      const originalValue = word.end_time?.toFixed(2) || '0.00';
      setHasUnsavedTimeChanges(startTime !== (word.start_time?.toFixed(2) || '0.00') || value !== originalValue);
    }
    // Clear any existing warnings when user is typing
    setOverlapWarning(null);
    setBoundaryConfirm(null);
    setPendingChange(null);
  };

  // Apply time changes manually
  const handleApplyTimeChanges = () => {
    // Apply start time if changed
    const startNum = parseFloat(startTime);
    const endNum = parseFloat(endTime);
    
    if (isNaN(startNum) || isNaN(endNum) || startNum < 0 || endNum < 0) {
      return;
    }

    // Check all overlaps and boundary changes
    const startOverlap = checkOverlap('start_time', startTime);
    const endOverlap = checkOverlap('end_time', endTime);
    
    if (startOverlap && startOverlap.type !== 'invalid') {
      setOverlapWarning(startOverlap);
      setPendingChange({ field: 'start_time', value: startNum, endValue: endNum });
      return;
    }
    
    if (endOverlap && endOverlap.type !== 'invalid') {
      setOverlapWarning(endOverlap);
      setPendingChange({ field: 'end_time', value: endNum, startValue: startNum });
      return;
    }

    // Check for boundary changes
    const startBoundary = checkBoundaryChange('start_time', startTime);
    const endBoundary = checkBoundaryChange('end_time', endTime);
    
    if (startBoundary) {
      setBoundaryConfirm({ ...startBoundary, field: 'start_time', value: startNum, endValue: endNum });
      setPendingChange({ field: 'start_time', value: startNum, endValue: endNum });
      return;
    }
    
    if (endBoundary) {
      setBoundaryConfirm({ ...endBoundary, field: 'end_time', value: endNum, startValue: startNum });
      setPendingChange({ field: 'end_time', value: endNum, startValue: startNum });
      return;
    }

    // No issues, apply both changes
    const newWords = captionData.words.map(w =>
      w.id === wordId ? { ...w, start_time: startNum, end_time: endNum } : w
    );
    
    let newSkeleton = captionData.skeleton;
    if (isFirstWordInSkeleton) {
      newSkeleton = newSkeleton.map((s, i) =>
        i === skeletonIndex ? { ...s, start_time: startNum } : s
      );
    }
    if (isLastWordInSkeleton) {
      newSkeleton = newSkeleton.map((s, i) =>
        i === skeletonIndex ? { ...s, end_time: endNum } : s
      );
    }
    
    updateCaptionData({ words: newWords, skeleton: newSkeleton });
    markWordsAsChanged(wordId);
    setHasUnsavedTimeChanges(false);
    setOverlapWarning(null);
    setPendingChange(null);
    setBoundaryConfirm(null);
  };

  const handleTimeBlur = (field, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      // Reset to original value
      if (field === 'start_time') {
        setStartTime(word.start_time?.toFixed(2) || '0.00');
      } else {
        setEndTime(word.end_time?.toFixed(2) || '0.00');
      }
      return;
    }

    // First check for overlaps
    const overlap = checkOverlap(field, value);
    
    if (overlap) {
      if (overlap.type === 'invalid') {
        setOverlapWarning({ ...overlap, field, value: numValue });
        setPendingChange(null);
        setBoundaryConfirm(null);
      } else {
        setOverlapWarning(overlap);
        setPendingChange({ field, value: numValue });
        setBoundaryConfirm(null);
      }
      return;
    }

    // Then check for boundary changes
    const boundary = checkBoundaryChange(field, value);
    
    if (boundary) {
      setBoundaryConfirm({ ...boundary, field, value: numValue });
      setPendingChange({ field, value: numValue });
      setOverlapWarning(null);
      return;
    }

    // No issues, apply change directly - update word and skeleton in single update
    const newWords = captionData.words.map(w =>
      w.id === wordId ? { ...w, [field]: numValue } : w
    );
    
    let newSkeleton = captionData.skeleton;
    if (field === 'start_time' && isFirstWordInSkeleton) {
      newSkeleton = captionData.skeleton.map((s, i) =>
        i === skeletonIndex ? { ...s, start_time: numValue } : s
      );
    } else if (field === 'end_time' && isLastWordInSkeleton) {
      newSkeleton = captionData.skeleton.map((s, i) =>
        i === skeletonIndex ? { ...s, end_time: numValue } : s
      );
    }
    
    updateCaptionData({ words: newWords, skeleton: newSkeleton });
    markWordsAsChanged(wordId);
    
    setOverlapWarning(null);
    setPendingChange(null);
    setBoundaryConfirm(null);
  };

  const handleApplyAnyway = () => {
    if (pendingChange) {
      // Get both time values - use pending or current input
      const startNum = pendingChange.field === 'start_time' ? pendingChange.value : (pendingChange.startValue ?? parseFloat(startTime));
      const endNum = pendingChange.field === 'end_time' ? pendingChange.value : (pendingChange.endValue ?? parseFloat(endTime));
      
      // Update both word and skeleton in a single update
      const newWords = captionData.words.map(w =>
        w.id === wordId ? { ...w, start_time: startNum, end_time: endNum } : w
      );
      
      let newSkeleton = captionData.skeleton;
      if (isFirstWordInSkeleton) {
        newSkeleton = newSkeleton.map((s, i) =>
          i === skeletonIndex ? { ...s, start_time: startNum } : s
        );
      }
      if (isLastWordInSkeleton) {
        newSkeleton = newSkeleton.map((s, i) =>
          i === skeletonIndex ? { ...s, end_time: endNum } : s
        );
      }
      
      updateCaptionData({ words: newWords, skeleton: newSkeleton });
      markWordsAsChanged(wordId);
    }
    setOverlapWarning(null);
    setPendingChange(null);
    setHasUnsavedTimeChanges(false);
  };

  const handleAutoAdjust = () => {
    if (overlapWarning && pendingChange) {
      // Get both time values
      const startNum = pendingChange.field === 'start_time' ? pendingChange.value : (pendingChange.startValue ?? parseFloat(startTime));
      const endNum = pendingChange.field === 'end_time' ? pendingChange.value : (pendingChange.endValue ?? parseFloat(endTime));
      
      // Update all affected data in a single update
      let newWords = captionData.words.map(w =>
        w.id === wordId ? { ...w, start_time: startNum, end_time: endNum } : w
      );
      
      let newSkeleton = [...captionData.skeleton];
      
      // Adjust the adjacent word to not overlap and update its skeleton if needed
      if (overlapWarning.type === 'overlap_prev' && overlapWarning.affectedWord) {
        const affectedWordId = overlapWarning.affectedWord.id;
        newWords = newWords.map(w =>
          w.id === affectedWordId ? { ...w, end_time: startNum } : w
        );
        
        // Check if affected word is the last word of its skeleton
        for (let i = 0; i < newSkeleton.length; i++) {
          const skel = newSkeleton[i];
          if (skel.words[skel.words.length - 1] === affectedWordId) {
            newSkeleton[i] = { ...skel, end_time: startNum };
            break;
          }
        }
      } else if (overlapWarning.type === 'overlap_next' && overlapWarning.affectedWord) {
        const affectedWordId = overlapWarning.affectedWord.id;
        newWords = newWords.map(w =>
          w.id === affectedWordId ? { ...w, start_time: endNum } : w
        );
        
        // Check if affected word is the first word of its skeleton
        for (let i = 0; i < newSkeleton.length; i++) {
          const skel = newSkeleton[i];
          if (skel.words[0] === affectedWordId) {
            newSkeleton[i] = { ...skel, start_time: endNum };
            break;
          }
        }
      }
      
      // Update skeleton times if current word is a boundary word
      if (isFirstWordInSkeleton) {
        newSkeleton = newSkeleton.map((s, i) =>
          i === skeletonIndex ? { ...s, start_time: startNum } : s
        );
      }
      if (isLastWordInSkeleton) {
        newSkeleton = newSkeleton.map((s, i) =>
          i === skeletonIndex ? { ...s, end_time: endNum } : s
        );
      }
      
      updateCaptionData({ words: newWords, skeleton: newSkeleton });
      markWordsAsChanged(wordId);
    }
    setOverlapWarning(null);
    setPendingChange(null);
    setHasUnsavedTimeChanges(false);
  };

  const handleCancelChange = () => {
    // Reset to original values
    setStartTime(word.start_time?.toFixed(2) || '0.00');
    setEndTime(word.end_time?.toFixed(2) || '0.00');
    setOverlapWarning(null);
    setPendingChange(null);
    setBoundaryConfirm(null);
    setHasUnsavedTimeChanges(false);
  };

  const handleConfirmBoundaryChange = () => {
    if (pendingChange) {
      // Get both time values
      const startNum = pendingChange.field === 'start_time' ? pendingChange.value : (pendingChange.startValue ?? parseFloat(startTime));
      const endNum = pendingChange.field === 'end_time' ? pendingChange.value : (pendingChange.endValue ?? parseFloat(endTime));
      
      // Update both word and skeleton in a single update to avoid stale state issues
      const newWords = captionData.words.map(w =>
        w.id === wordId ? { ...w, start_time: startNum, end_time: endNum } : w
      );
      
      let newSkeleton = captionData.skeleton;
      if (isFirstWordInSkeleton) {
        newSkeleton = newSkeleton.map((s, i) =>
          i === skeletonIndex ? { ...s, start_time: startNum } : s
        );
      }
      if (isLastWordInSkeleton) {
        newSkeleton = newSkeleton.map((s, i) =>
          i === skeletonIndex ? { ...s, end_time: endNum } : s
        );
      }
      
      updateCaptionData({ words: newWords, skeleton: newSkeleton });
      markWordsAsChanged(wordId);
    }
    setBoundaryConfirm(null);
    setPendingChange(null);
    setHasUnsavedTimeChanges(false);
  };

  const handleToggleBreak = () => {
    updateWord(wordId, { break: !word.break });
  };

  const handleRemoveLineBreak = () => {
    if (skeletonIndex > 0) {
      mergeWithPrevious(skeletonIndex);
      onClose();
    }
  };

  const handleMergeWithNextChunk = () => {
    if (skeletonIndex < captionData.skeleton.length - 1) {
      mergeSkeletons([skeletonIndex, skeletonIndex + 1]);
      onClose();
    }
  };

  const handleSplitLine = () => {
    if (wordIndexInSkeleton > 0) {
      splitSkeleton(skeletonIndex, wordIndexInSkeleton);
      onClose();
    }
  };

  const handleAddWord = () => {
    addWord(skeletonIndex, wordId, 'جديد');
  };

  const handleRemoveWord = () => {
    if (skeleton.words.length > 1) {
      removeWord(skeletonIndex, wordId);
      onClose();
    }
  };

  const handleSplitWord = () => {
    const result = splitWord(skeletonIndex, wordId, isArabizi);
    if (result.success) {
      onClose();
    }
  };

  const handleMergeWithPrevious = () => {
    const result = mergeWords(skeletonIndex, wordId, 'previous');
    if (result.success) {
      onClose();
    }
  };

  const handleMergeWithNext = () => {
    const result = mergeWords(skeletonIndex, wordId, 'next');
    if (result.success) {
      onClose();
    }
  };

  const handleEditWord = () => {
    // Store original text before editing to compare later
    const currentText = isArabizi ? (word.arabizi || word.text || '') : (word.text || '');
    setOriginalTextBeforeEdit(currentText);
    setShowKeyboard(true);
  };

  // Auto-save on keyboard close - checks for duplicate words
  const handleKeyboardClose = () => {
    const trimmedText = editText.trim();
    if (!trimmedText) {
      setShowKeyboard(false);
      return;
    }
    
    // Check if text actually changed
    const textChanged = isArabizi 
      ? trimmedText !== (word.arabizi || word.text)
      : trimmedText !== word.text;
    
    if (!textChanged) {
      setShowKeyboard(false);
      return;
    }
    
    // Find other words with the same original text
    const matchingWords = captionData.words.filter(w => {
      if (w.id === wordId) return false; // Exclude current word
      if (w.type === 'punctuation' || w.type === 'silence') return false; // Exclude non-words
      
      const wordText = isArabizi ? (w.arabizi || w.text || '') : (w.text || '');
      return wordText === originalTextBeforeEdit;
    });
    
    // If there are other matching words, show the replace all modal
    if (matchingWords.length > 0) {
      setReplaceAllInfo({
        count: matchingWords.length + 1, // +1 for current word
        newText: trimmedText,
        matchingWordIds: matchingWords.map(w => w.id)
      });
      setShowReplaceAllModal(true);
      setShowKeyboard(false);
    } else {
      // No duplicates, save normally
      if (isArabizi) {
        updateWord(wordId, { arabizi: trimmedText });
      } else {
        updateWord(wordId, { text: trimmedText });
      }
      setShowKeyboard(false);
    }
  };

  // Replace all matching words with new text
  const handleReplaceAll = () => {
    const { newText, matchingWordIds } = replaceAllInfo;
    
    // Update all matching words including current word
    const allWordIdsToUpdate = [wordId, ...matchingWordIds];
    const newWords = captionData.words.map(w => {
      if (allWordIdsToUpdate.includes(w.id)) {
        if (isArabizi) {
          return { ...w, arabizi: newText };
        } else {
          return { ...w, text: newText };
        }
      }
      return w;
    });
    
    updateCaptionData({ ...captionData, words: newWords });
    markWordsAsChanged(allWordIdsToUpdate);
    setShowReplaceAllModal(false);
    setReplaceAllInfo({ count: 0, newText: '', matchingWordIds: [] });
  };

  // Replace only the current word
  const handleReplaceOnlyThis = () => {
    const { newText } = replaceAllInfo;
    
    if (isArabizi) {
      updateWord(wordId, { arabizi: newText });
    } else {
      updateWord(wordId, { text: newText });
    }
    
    setShowReplaceAllModal(false);
    setReplaceAllInfo({ count: 0, newText: '', matchingWordIds: [] });
  };

  // Cancel the replace all modal - close the entire popup without saving
  const handleCancelReplaceAll = () => {
    setShowReplaceAllModal(false);
    setReplaceAllInfo({ count: 0, newText: '', matchingWordIds: [] });
    onClose(); // Close the entire popup
  };

  const canRemoveLineBreak = skeletonIndex > 0;
  const canMergeWithNextChunk = skeletonIndex < captionData.skeleton.length - 1;
  const canSplitLine = wordIndexInSkeleton > 0;
  const canRemoveWord = skeleton.words.length > 1;
  const isLastWord = wordIndexInSkeleton === skeleton.words.length - 1;

  // Handle phrase alternative selection
  const handleSelectPhraseAlternative = (alternativeId) => {
    if (!phrase) return;
    
    const result = selectPhraseAlternative(phrase.id, alternativeId);
    
    if (result.needsMerge) {
      // Show merge confirmation
      setPhraseMergeConfirm({
        skeletonIndexes: result.skeletonIndexes,
        message: result.message
      });
      setPendingPhraseAlternative(alternativeId);
    } else if (result.success) {
      // Successfully applied - close popup so user can click on new word to edit
      onClose();
    } else {
      console.error('Failed to select alternative:', result.error);
    }
  };

  const handleConfirmPhraseMerge = () => {
    if (phraseMergeConfirm && phraseMergeConfirm.skeletonIndexes && phrase) {
      // Use the atomic merge-and-replace function to do both operations in one state update
      // This fixes the issue where merge would happen but replacement wouldn't
      const result = mergeSkeletonsAndApplyAlternative(
        phraseMergeConfirm.skeletonIndexes,
        phrase.id,
        pendingPhraseAlternative
      );
      
      if (result.success) {
        // Close popup so user can click on new word to edit
        onClose();
      } else {
        console.error('Failed to merge and apply alternative:', result.error);
      }
    }
    setPhraseMergeConfirm(null);
    setPendingPhraseAlternative(null);
  };

  const handleCancelPhraseMerge = () => {
    setPhraseMergeConfirm(null);
    setPendingPhraseAlternative(null);
  };

  // Punctuation handlers
  const handleEditPunctuation = () => {
    if (attachedPunctuation) {
      setPunctuationText(attachedPunctuation.text);
      setEditingPunctuation(true);
    }
  };

  const handleSavePunctuation = () => {
    if (attachedPunctuation && punctuationText.trim()) {
      updateWord(attachedPunctuation.id, { text: punctuationText.trim() });
    }
    setEditingPunctuation(false);
  };

  const handleDeletePunctuation = () => {
    if (attachedPunctuation) {
      removeWord(skeletonIndex, attachedPunctuation.id);
    }
  };

  const handleCancelPunctuationEdit = () => {
    setEditingPunctuation(false);
    setPunctuationText('');
  };

  // Play only this word
  const handlePlayWord = () => {
    if (videoRef?.current && word && word.start_time !== undefined) {
      // Notify parent to lock highlight on this word
      if (window.lockWordHighlight) {
        window.lockWordHighlight(word.id);
      }
      
      seekToTime(word.start_time);
      videoRef.current.play();
      
      // Pause at the end of the word and unlock highlight
      const checkTime = () => {
        if (videoRef.current.currentTime >= word.end_time) {
          videoRef.current.pause();
          videoRef.current.removeEventListener('timeupdate', checkTime);
          
          // Unlock highlight after a short delay
          setTimeout(() => {
            if (window.unlockWordHighlight) {
              window.unlockWordHighlight();
            }
          }, 100);
        }
      };
      videoRef.current.addEventListener('timeupdate', checkTime);
    }
  };

  return createPortal(
    <div 
      className="word-popup"
      ref={popupRef}
      style={{ 
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 9999,
        maxHeight: maxHeight ? `${maxHeight}px` : 'calc(100vh - 40px)',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      {showKeyboard ? (
        <div className="word-edit-mode">
          <ArabicKeyboard
            value={editText}
            onChange={setEditText}
            onClose={handleKeyboardClose}
          />
        </div>
      ) : showReplaceAllModal ? (
        <div className="replace-all-modal">
          <div className="replace-all-header">
            <div className="replace-all-title">{t('Multiple Occurrences')}</div>
          </div>
          
          <div className="replace-all-content">
            <div className="replace-all-preview">
              <div className="replace-from">
                <span className="replace-label">{t('From')}</span>
                <span className="replace-text" dir={isArabizi ? 'ltr' : 'rtl'}>{originalTextBeforeEdit}</span>
              </div>
              <div className="replace-arrow">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
              <div className="replace-to">
                <span className="replace-label">{t('To')}</span>
                <span className="replace-text highlight" dir={isArabizi ? 'ltr' : 'rtl'}>{replaceAllInfo.newText}</span>
              </div>
            </div>
            
            <p className="replace-all-message">
              {rich(t('Found <b>{count} times</b>', { count: replaceAllInfo.count }), { b: (c) => <strong>{c}</strong> })}
            </p>
          </div>
          
          <div className="replace-all-actions">
            <button className="replace-btn replace-all" onClick={handleReplaceAll}>
              {t('Replace All ({count})', { count: replaceAllInfo.count })}
            </button>
            <button className="replace-btn replace-one" onClick={handleReplaceOnlyThis}>
              {t('Only This')}
            </button>
            <button className="replace-btn replace-cancel" onClick={handleCancelReplaceAll}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Word Preview */}
          <div className="popup-section word-preview-section">
            <span className="word-preview" dir={isArabizi ? 'ltr' : 'rtl'}>{getDisplayText(word)}</span>
            <div className="word-preview-actions">
              <button className="edit-word-btn tooltip-trigger" onClick={handlePlayWord} data-tooltip={t('Play this word')} aria-label={t('Play this word')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
              <button className="edit-word-btn tooltip-trigger" onClick={handleEditWord} data-tooltip={t('Edit word text')} aria-label={t('Edit word text')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Phrase Alternatives (hidden for Latin-script languages — words already have correct text) */}
          {phraseDisplayInfo && phraseDisplayInfo.hasAlternatives && !isNonArabic && (
            <div className="popup-section phrase-section">
              <button 
                className={`phrase-toggle-btn ${showPhraseAlternatives ? 'expanded' : ''}`}
                onClick={() => setShowPhraseAlternatives(!showPhraseAlternatives)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
                  <path d="M9 18l6-6-6-6" />
                </svg>
                <span className="phrase-toggle-label">{t('Alternatives')}</span>
              </button>
              
              {showPhraseAlternatives && (
                <div className="phrase-alternatives-panel">
                  {phraseMergeConfirm ? (
                    <div className="phrase-merge-confirm">
                      <div className="merge-warning-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                        </svg>
                      </div>
                      <div className="merge-warning-content">
                        <p className="merge-warning-title">{t('Merge Lines Required')}</p>
                        <p className="merge-warning-message">{t(phraseMergeConfirm.message)}</p>
                        <div className="merge-warning-actions">
                          <button className="merge-btn confirm" onClick={handleConfirmPhraseMerge}>
                            {t('Merge & Apply')}
                          </button>
                          <button className="merge-btn cancel" onClick={handleCancelPhraseMerge}>
                            {t('Cancel')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Alternative Options - only show non-active, filtered types */}
                      {phrase.alternatives
                        .filter(alt => {
                          // Skip currently active
                          if (phrase.activeAlternativeId === alt.id) return false;
                          // Skip certain types by label
                          const lowerLabel = (alt.label || '').toLowerCase();
                          if (lowerLabel.includes('original')) return false;
                          if (lowerLabel.includes('foreign')) return false;
                          if (lowerLabel.includes('loanword')) return false;
                          return true;
                        })
                        .map((alt) => {
                          const altText = alt.words.map(w => isArabizi ? (w.arabizi || w.text) : w.text).join(' ');
                          
                          return (
                            <button
                              key={alt.id}
                              className="alternative-option"
                              onClick={() => handleSelectPhraseAlternative(alt.id)}
                            >
                              <span 
                                className="alternative-text" 
                                dir={isArabizi ? 'ltr' : 'rtl'}
                                style={{ fontFamily: themeSettings.proseFontFamily || 'Noto Naskh Arabic' }}
                              >
                                {altText}
                              </span>
                            </button>
                          );
                        })}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Attached Punctuation Section */}
          {attachedPunctuation && (
            <div className="popup-section punctuation-section">
              <div className="popup-section-header">
                <span className="popup-label">{t('Attached Punctuation')}</span>
                {useLatinPunctuation && <span className="punct-script-badge">{t('Latin')}</span>}
              </div>
              {editingPunctuation ? (
                <div className="punctuation-edit">
                  <input
                    ref={punctuationInputRef}
                    type="text"
                    className="punct-input"
                    value={punctuationText}
                    onChange={(e) => setPunctuationText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSavePunctuation();
                      } else if (e.key === 'Escape') {
                        handleCancelPunctuationEdit();
                      }
                    }}
                    dir={useLatinPunctuation ? 'ltr' : 'rtl'}
                    autoFocus
                  />
                  <div className="punct-keyboard">
                    {punctuationChars.map((char) => (
                      <button
                        key={char}
                        className="punct-key"
                        onClick={() => {
                          setPunctuationText(prev => prev + char);
                          punctuationInputRef.current?.focus();
                        }}
                      >
                        {char}
                      </button>
                    ))}
                    <button
                      className="punct-key backspace"
                      onClick={() => {
                        setPunctuationText(prev => prev.slice(0, -1));
                        punctuationInputRef.current?.focus();
                      }}
                    >
                      ⌫
                    </button>
                    <button
                      className="punct-key clear"
                      onClick={() => {
                        setPunctuationText('');
                        punctuationInputRef.current?.focus();
                      }}
                    >
                      {t('Clear')}
                    </button>
                  </div>
                  <div className="punct-edit-buttons">
                    <button className="punct-btn save" onClick={handleSavePunctuation}>
                      {t('Save')}
                    </button>
                    <button className="punct-btn cancel" onClick={handleCancelPunctuationEdit}>
                      {t('Cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="punctuation-display">
                  <span className="punctuation-text" dir="rtl">{attachedPunctuation.text}</span>
                  <div className="punctuation-actions">
                    <button className="punct-action-btn tooltip-trigger" onClick={handleEditPunctuation} data-tooltip={t('Edit punctuation')} aria-label={t('Edit punctuation')}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button className="punct-action-btn danger tooltip-trigger" onClick={handleDeletePunctuation} data-tooltip={t('Delete punctuation')} aria-label={t('Delete punctuation')}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Time Range - Hidden for punctuation */}
          {!isPunctuation && (
          <div className="popup-section time-section-new">
            {/* Compact Timeline Bar */}
            <div className="timeline-bar" dir="ltr">
              <div className="timeline-track">
                <div className="timeline-fill" style={{ 
                  left: '0%', 
                  width: '100%' 
                }}></div>
              </div>
              <div className="timeline-labels">
                <span className="timeline-start">{parseFloat(startTime).toFixed(2)}s</span>
                <span className="timeline-duration">
                  {((parseFloat(endTime) || 0) - (parseFloat(startTime) || 0)).toFixed(2)}s
                </span>
                <span className="timeline-end">{parseFloat(endTime).toFixed(2)}s</span>
              </div>
            </div>

            {/* Inline Time Inputs */}
            <div className="time-inputs-row" dir="ltr" lang="en">
              <div className="time-input-group">
                <label className="time-label">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  {t('Start')}
                </label>
                <div className="time-input-wrapper">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={startTime}
                    onChange={(e) => handleTimeInputChange('start_time', e.target.value)}
                    onBlur={(e) => handleTimeBlur('start_time', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    className={`time-input-compact ${overlapWarning?.field === 'start_time' || boundaryConfirm?.field === 'start_time' ? 'warning' : ''}`}
                  />
                  <span className="time-suffix">s</span>
                </div>
              </div>

              <div className="time-arrow">→</div>

              <div className="time-input-group">
                <label className="time-label">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="12" height="16" rx="2" />
                  </svg>
                  {t('End')}
                </label>
                <div className="time-input-wrapper">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={endTime}
                    onChange={(e) => handleTimeInputChange('end_time', e.target.value)}
                    onBlur={(e) => handleTimeBlur('end_time', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    className={`time-input-compact ${overlapWarning?.field === 'end_time' || boundaryConfirm?.field === 'end_time' ? 'warning' : ''}`}
                  />
                  <span className="time-suffix">s</span>
                </div>
              </div>
            </div>

            {/* Boundary Change Confirmation */}
            {boundaryConfirm && !overlapWarning && (
              <div className="boundary-warning">
                <div className="warning-icon info">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </div>
                <div className="warning-content">
                  <p className="warning-message">{boundaryConfirm.message}</p>
                  <p className="warning-hint">{t('This word is at the chunk boundary')}</p>
                  <div className="warning-actions">
                    <button className="warning-btn confirm" onClick={handleConfirmBoundaryChange}>
                      {t('Confirm')}
                    </button>
                    <button className="warning-btn cancel" onClick={handleCancelChange}>
                      {t('Cancel')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Apply Button - shown when there are unsaved changes */}
            {hasUnsavedTimeChanges && !overlapWarning && !boundaryConfirm && (
              <div className="time-apply-row">
                <button className="time-apply-btn" onClick={handleApplyTimeChanges}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {t('Apply Changes')}
                </button>
                <button className="time-reset-btn" onClick={handleCancelChange}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            )}

            {/* Overlap Warning */}
            {overlapWarning && (
              <div className="overlap-warning">
                <div className="warning-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
                  </svg>
                </div>
                <div className="warning-content">
                  <p className="warning-message">{overlapWarning.message}</p>
                  <p className="warning-hint">
                    {overlapWarning.type === 'overlap_prev' 
                      ? t('Auto-adjust will shorten the previous word') 
                      : overlapWarning.type === 'overlap_next'
                      ? t('Auto-adjust will delay the next word')
                      : ''}
                  </p>
                  {overlapWarning.type !== 'invalid' && (
                    <div className="warning-actions">
                      <button className="warning-btn auto-adjust" onClick={handleAutoAdjust}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                        </svg>
                        {t('Auto-adjust')}
                      </button>
                      <button className="warning-btn cancel" onClick={handleCancelChange}>
                        {t('Cancel')}
                      </button>
                    </div>
                  )}
                  {overlapWarning.type === 'invalid' && (
                    <div className="warning-actions">
                      <button className="warning-btn cancel" onClick={handleCancelChange}>
                        {t('OK')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
          )}

          {/* Actions */}
          <div className="popup-actions">
            <div className="popup-action-group">
              <div className="popup-action-title">{t('Chunk')}</div>
              <div className="popup-action-list">
                <button 
                  className={`popup-action-btn ${!canRemoveLineBreak ? 'disabled' : ''}`}
                  onClick={handleRemoveLineBreak}
                  disabled={!canRemoveLineBreak}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  <span>{t('Merge with previous chunk')}</span>
                </button>

                <button 
                  className={`popup-action-btn ${!canMergeWithNextChunk ? 'disabled' : ''}`}
                  onClick={handleMergeWithNextChunk}
                  disabled={!canMergeWithNextChunk}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="10 8 14 12 10 16" />
                  </svg>
                  <span>{t('Merge with next chunk')}</span>
                </button>

                <button 
                  className={`popup-action-btn ${!canSplitLine ? 'disabled' : ''}`}
                  onClick={handleSplitLine}
                  disabled={!canSplitLine}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="6" cy="6" r="3" />
                    <circle cx="6" cy="18" r="3" />
                    <path d="M6 9v6" />
                    <path d="M13 6h8" />
                    <path d="M13 18h8" />
                  </svg>
                  <span>{t('Split into new chunk')}</span>
                </button>
              </div>
            </div>

            <div className="popup-action-group">
              <div className="popup-action-title">{t('Word')}</div>
              <div className="popup-action-list">
                {/* Toggle speaker dash — gated by REACT_APP_ENABLE_SPEAKER_FEATURES */}
                {process.env.REACT_APP_ENABLE_SPEAKER_FEATURES === 'true' && !isPunctuation && (() => {
                  const text = word.text || '';
                  const hasTextDash = text.startsWith('– ') || text.startsWith('- ');
                  const isSuppressed = word.speakerDash === false;
                  const hasAutoDash = isAutoSpeakerChange && !isSuppressed;
                  const showingDash = hasTextDash || hasAutoDash;

                  return (
                    <button
                      className={`popup-action-btn ${showingDash ? 'active-toggle' : ''} ${isSuppressed ? 'suppressed-toggle' : ''}`}
                      onClick={() => {
                        if (hasTextDash) {
                          const cleaned = text.replace(/^–\s?|^-\s?/, '');
                          updateWord(wordId, { text: cleaned });
                        } else if (hasAutoDash) {
                          updateWord(wordId, { speakerDash: false });
                        } else if (isSuppressed) {
                          updateWord(wordId, { speakerDash: undefined });
                        } else {
                          updateWord(wordId, { text: '– ' + text });
                        }
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14" />
                      </svg>
                      <span>{t(showingDash ? 'Remove speaker dash' : isSuppressed ? 'Restore speaker dash' : 'Add speaker dash')}</span>
                    </button>
                  );
                })()}

                {canSplitWord && (
                  <button
                    className="popup-action-btn"
                    onClick={handleSplitWord}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 3h5v5" />
                      <path d="M8 3H3v5" />
                      <path d="M12 22v-8.3a4 4 0 00-1.172-2.872L3 3" />
                      <path d="M15 9l6-6" />
                    </svg>
                    <span>{t('Split word')}</span>
                  </button>
                )}

                {(canMergeWithPrevious || canMergeWithNext) && (
                  <div className="merge-word-row">
                    <button
                      className={`merge-option-btn ${!canMergeWithPrevious ? 'disabled' : ''}`}
                      onClick={handleMergeWithPrevious}
                      disabled={!canMergeWithPrevious}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 17l-5-5 5-5" />
                        <path d="M18 17l-5-5 5-5" />
                      </svg>
                      <span>{t('Merge previous')}</span>
                    </button>
                    <button
                      className={`merge-option-btn ${!canMergeWithNext ? 'disabled' : ''}`}
                      onClick={handleMergeWithNext}
                      disabled={!canMergeWithNext}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 7l5 5-5 5" />
                        <path d="M6 7l5 5-5 5" />
                      </svg>
                      <span>{t('Merge next')}</span>
                    </button>
                  </div>
                )}

                <button
                  className={`popup-action-btn danger ${!canRemoveWord ? 'disabled' : ''}`}
                  onClick={handleRemoveWord}
                  disabled={!canRemoveWord}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  </svg>
                  <span>{t('Remove word')}</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

    </div>,
    document.body
  );
};

export default WordPopup;
